import React, {useEffect, useState} from 'react';
import {
  Stack,
  Box,
  Typography,
  IconButton,
  useTheme,
  TextField,
  Card,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Checkbox,
  ListItemText
} from '@mui/material';
import Dialog, {dialogClasses} from '@mui/material/Dialog';
import {useForm} from 'react-hook-form';
import {LoadingButton} from '@mui/lab';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight
} from '@mui/icons-material';
import {jwtDecode} from 'jwt-decode';
import moment from 'moment';
import {fCurrency} from 'src/utils/format-number';
import EditIcon from '@mui/icons-material/Edit';
import {
  useGridApiRef,
  GridColDef,
  useGridApiContext
} from '@mui/x-data-grid-premium';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import {useSnackbar} from 'notistack';
import {DatePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import FormProvider from 'src/minimal-components/hook-form';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {GridCellRender} from '../../grid-utils';
import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import EmptyContent from '@components/empty-content';
import ListSubheaderComponent from '../list-subheader/ListSubheader';
import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import {useBoolean} from 'src/hooks/use-boolean';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {FormControlDiv} from '../../../module-search-filter/ModuleSearchFilterStyle';
import SelectInput from '@components/form/select-input/SelectInput';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';

import * as APIService from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {rowStyles} from '@shared/utils/HelperUtils';
import {APP} from 'src/styles/variables';
import {deepCopy} from '../../util';

import {FlexGridCell, FlexDiv, CardSpan} from './PaymentEntryStyles';
import {useAppContext} from '@context/AppContext';

const MultiSelectEditCell = (props: any) => {
  const {id, field, value} = props.params;
  const apiRef = useGridApiContext();

  const parsedValue =
    typeof value === 'string'
      ? value.split(',').map(v => v.trim())
      : value || [];

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue
    });
  };

  return (
    <Select
      multiple
      value={parsedValue}
      onChange={handleChange}
      fullWidth
      renderValue={selected => (selected || []).join(', ')}>
      {(props?.options || []).map((option: any) => (
        <MenuItem key={option.value} value={option.value}>
          <Checkbox checked={parsedValue.includes(option.value)} />
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </Select>
  );
};

const PaymentEntry = (props: any) => {
  const serviceLineOpen = useBoolean();
  const {enqueueSnackbar} = useSnackbar();
  const [serviceLinesRows, setServiceLinesRows] = useState<any>([]);
  const [serviceLinesRowsCopy, setServiceLinesRowsCopy] = useState<any>([]);
  const [paymentPage, setPaymentPage] = useState<any>(1);
  const [inputPage, setInputPage] = useState<any>(1);
  const [totalPaymentPages, setTotalPaymentPages] = useState<any>(0);
  const [isNewPayment, setIsNewPayment] = useState(false);
  const [usersInfo, setUsersInfo] = useState<any>([]);
  const [accountNumber, setAccountNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientFirstName, setPatientFirstName] = useState('');
  const [MRN, setMRN] = useState('');

  const [payerMaster, setPayerMaster] = useState<any>([]);
  const [payCodes, setPayCodes] = useState<any>([]);
  const [adjustmentCodes, setAdjustmentCodes] = useState<any>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [payCodesLoading, setPayCodesLoading] = useState(false);
  const [adjustmentCodesLoading, setAdjustmentCodesLoading] = useState(false);
  const [payerMasterLoading, setPayerMasterLoading] = useState(false);

  const [selectedPayer, setSelectedPayer] = useState('');
  const [selectedPayCode, setSelectedPayCode] = useState('');
  const [selectedAdjustmentCode, setSelectedAdjustmentCode] = useState('');
  const [selectedAgencyPayCode, setSelectedAgencyPayCode] = useState('');
  const [agencyPayCodes, setAgencyPayCodes] = useState<any>([]);
  const [denialReasonCodes, setDenialReasonCodes] = useState<any>([]);
  const [transactionStatusCodes, setTransactionStatusCodes] = useState<any>([]);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState<number | null>(null);
  const [serviceDate, setServiceDate] = useState(null);

  const [payerDisabled, setPayerDisabled] = useState(false);
  const [adjustmentCodeDisabled, setAdjustmentCodeDisabled] = useState(false);
  const [payCodeDisabled, setPayCodeDisabled] = useState(false);

  const [paymentType, setPaymentType] = useState('INSURANCE');
  const [applyButtonLoading, setApplyButtonLoading] = useState(false);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [reCalculateButtonLoading, setReCalculateButtonLoading] =
    useState(false);
  const [defaultTrackingDescription, setDefaultTrackingDescription] = useState(
    props?.batchInfo?.DefaultTracking || ''
  );
  const [trackingDescription, setTrackingDescription] = useState(
    props?.batchInfo?.DefaultTracking || ''
  );
  const [batchDetail, setBatchDetail] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [serviceLinesLoading, setServiceLinesLoading] = useState(false);
  const [pageDisabled, setPageDisabled] = useState(false);
  const [removeBatchDetailDialog, setRemoveBatchDetailDialog] = useState(false);
  const [removeBatchDetailLoading, setRemoveBatchDetailLoading] =
    useState(false);
  const [clearTransactionsLoading, setClearTransactionsLoading] =
    useState(false);
  const [clearTransactionsDialog, setClearTransactionsDialog] = useState(false);
  const [shouldDisplayEditIcon, setShouldDisplayEditIcon] = useState(false);
  const [summaryAmounts, setSummaryAmounts] = useState<any>([]);
  const [pendingTransactions, setPendingTransactions] = useState<any>([]);
  const [displayCobWindow, setDisplayCobWindow] = useState(false);
  const [cobServiceLines, setCobServiceLines] = useState<any>([]);
  const [cobSummaryAmounts, setCobSummaryAmounts] = useState<any>([]);
  const [cobPendingTransactions, setCobPendingTransactions] = useState<any>([]);
  const [selectedCollectionAgency, setSelectedCollectionAgency] = useState('');
  const [collectionAgencies, setCollectionAgencies] = useState<any>([]);
  const [collectionAgenciesLoading, setCollectionAgenciesLoading] =
    useState<any>(false);
  const [selectedFinancialClass, setSelectedFinancialClass] = useState('');
  const [financialClasses, setFinancialClasses] = useState<any>([]);
  const [financialClassLoading, setFinancialClassLoading] = useState(false);
  const [totalBatchAmount, setTotalBatchAmount] = useState(null);
  const [totalBatchPostedAmount, setTotalBatchPostedAmount] = useState(null);
  const [totalBatchVarianceAmount, setTotalBatchVarianceAmount] =
    useState(null);
  const gridApiRef = useGridApiRef();
  const {
    appState: {applicationName}
  } = useAppContext();

  const paymentEntryMethods = useForm({});
  const theme = useTheme();
  let userData: any, userEmail: any;
  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
    userEmail = userData?.email || userData?.user?.email;
  }
  const transactionsSetter = displayCobWindow
    ? setCobPendingTransactions
    : setPendingTransactions;
  const currentServiceLines = displayCobWindow
    ? cobServiceLines
    : serviceLinesRows;
  const serviceLinesSetter = displayCobWindow
    ? setCobServiceLines
    : setServiceLinesRows;
  const currentTransactions = displayCobWindow
    ? cobPendingTransactions
    : pendingTransactions;

  const resetState = () => {
    setServiceLinesRows([]);
    setServiceLinesRowsCopy([]);
    setUsersInfo([]);
    setAccountNumber('');
    setInvoiceNumber('');
    setPatientLastName('');
    setPatientFirstName('');
    setMRN('');
    setPayerMaster([]);
    setPayCodes([]);
    setAdjustmentCodes([]);
    setSelectedPayer('');
    setSelectedPayCode('');
    setSelectedAdjustmentCode('');
    setSelectedAgencyPayCode('');
    setAgencyPayCodes([]);
    setPaymentAmount(null);
    setAdjustmentAmount(null);
    setServiceDate(null);
    setPaymentType('INSURANCE');
    setBatchDetail([]);
    setSummaryAmounts([]);
    setPendingTransactions([]);
    setCobServiceLines([]);
    setCobPendingTransactions([]);
    setCobSummaryAmounts([]);
  };

  const onCloseRemoveBatchDetailDialog = () => {
    setRemoveBatchDetailDialog(false);
  };

  const isSavedBatchDetail = () => {
    return batchDetail.length ? true : false;
  };

  const isCollectionAgencyPayment = () => {
    return paymentType === 'COLLECTION_AGENCY';
  };

  const isPatientPayment = () => {
    return paymentType === 'PATIENT';
  };

  const isInsurancePayment = () => {
    return paymentType === 'INSURANCE';
  };

  const isEmptyServiceLines = () => {
    return serviceLinesRows && serviceLinesRows.length ? false : true;
  };

  const getBatchDetailId = (batchDetailCopy: any) => {
    let batchDetailId = batchDetail.length ? batchDetail[0].id : null;
    if (!batchDetailId) {
      batchDetailId = batchDetailCopy.length ? batchDetailCopy[0].id : null;
    }
    return batchDetailId;
  };

  const getApplyButtonText = () => {
    if (isCollectionAgencyPayment()) {
      return 'Apply Collection Agency Payment';
    } else if (isPatientPayment()) {
      return 'Apply Patient Payment';
    }
    return 'Apply Payment';
  };

  const updatePatientInfo = (invoiceInfo: any) => {
    setMRN(invoiceInfo?.LegacyMRN);
    setPatientFirstName(invoiceInfo?.FirstName);
    setPatientLastName(invoiceInfo?.LastName);
  };

  const getPayCodes = async (
    payerMasterId: any,
    collectionAgencyId: any,
    financialClassTypeId: any,
    paymentTypeValue: any
  ) => {
    try {
      setPayCodesLoading(true);
      const tk = EncryptUtils.encryptedData({
        payerMasterId: payerMasterId || selectedPayer,
        collectionAgencyId: collectionAgencyId,
        financialClassTypeId: financialClassTypeId,
        paymentType: paymentTypeValue,
        paymentConfig: props?.paymentConfig,
        legacyApp: props?.batchInfo?.LegacyApp
      });
      const payCodesData = await APIService.getPayCodes({tk});
      if (payCodesData.data.length && !selectedPayCode) {
        setSelectedPayCode(payCodesData.data[0].ID);
      }
      setPayCodesLoading(false);
      setPayCodes([...payCodesData.data]);
    } catch (e) {
      setPayCodesLoading(false);
    }
  };

  const getAdjustmentCodes = async (
    payerMasterId: any,
    collectionAgencyId: any,
    financialClassTypeId: any,
    paymentTypeValue: any
  ) => {
    try {
      setAdjustmentCodesLoading(true);
      const tk = EncryptUtils.encryptedData({
        payerMasterId: payerMasterId || selectedPayer,
        collectionAgencyId: collectionAgencyId,
        financialClassTypeId: financialClassTypeId,
        paymentType: paymentTypeValue,
        paymentConfig: props?.paymentConfig,
        legacyApp: props?.batchInfo?.LegacyApp
      });
      const adjustmentCodesData = await APIService.getAdjustmentCodes({tk});
      setAdjustmentCodes([...adjustmentCodesData.data]);
      if (adjustmentCodesData.data.length && !selectedAdjustmentCode) {
        setSelectedAdjustmentCode(adjustmentCodesData.data[0].ID);
      }
      setAdjustmentCodesLoading(false);
    } catch (e) {
      setAdjustmentCodesLoading(false);
      return;
    }
  };

  const getSaveDisabledStatus = () => {
    // if (!pendingTransactions?.length) {
    //   return true;
    // }
    return serviceLinesRows && serviceLinesRows.length ? false : true;
  };

  const onClosePaymentEntry = () => {
    props?.onClosePaymentEntry();
  };

  const getAgencyPayCodes = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        legacyApp: props?.batchInfo?.LegacyApp,
        paymentConfig: props?.paymentConfig
      });
      const agencyPayCodesData = await APIService.getAgencyPayCodes({tk});
      setAgencyPayCodes(agencyPayCodesData?.data);
    } catch (e) {}
  };

  const getDenialReasonCodes = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        legacyApp: props?.batchInfo?.LegacyApp,
        paymentConfig: props?.paymentConfig
      });
      const denialReasonCodesData = await APIService.getDenialReasonCodes({tk});
      setDenialReasonCodes(denialReasonCodesData?.data);
    } catch (e) {}
  };

  const getTransactionStatusCodes = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        legacyApp: props?.batchInfo?.LegacyApp,
        paymentConfig: props?.paymentConfig
      });
      const statusCodesData = await APIService.getTransactionStatusCodes({tk});
      setTransactionStatusCodes(statusCodesData?.data);
    } catch (e) {}
  };

  const getPayerOrder = () => {
    const payerObj = payerMaster.find((pm: any) => pm.ID === selectedPayer);
    if (payerObj) {
      return payerObj.Order;
    }
    return 1;
  };

  const getSelectedPayerObject = () => {
    const payerObj = payerMaster.find((pm: any) => pm.ID === selectedPayer);
    return payerObj || {};
  };

  const getSelectedPayCodeObject = () => {
    if (selectedPayCode === 'NONE') {
      return {
        ID: null,
        PayCode: 'NONE',
        PayCodeDescription: ''
      };
    }
    const payCodeObj = payCodes.find((s: any) => s.ID === selectedPayCode);
    return payCodeObj || {};
  };

  const getSelectedAdjustmentCodeObj = () => {
    if (selectedAdjustmentCode === 'NONE') {
      return {
        ID: null,
        PayCode: 'NONE',
        PayCodeDescription: ''
      };
    }
    const adjustmentCodeObj = adjustmentCodes.find(
      (s: any) => s.ID === selectedAdjustmentCode
    );
    return adjustmentCodeObj || {};
  };

  const getSelectedCollectionAgencyObj = () => {
    if (selectedCollectionAgency) {
      return (
        collectionAgencies.find(
          (c: any) => c.ID === selectedCollectionAgency
        ) || {}
      );
    }
    return {};
  };

  const getInsuranceColumnBasedOnTransactionStatus = (
    transactionStatus: any
  ) => {
    if (['None', 'Appeal'].includes(transactionStatus)) {
      const payerOrder = getPayerOrder();
      let insuranceBucketCol = '';
      if (payerOrder >= 1 && payerOrder <= 3) {
        insuranceBucketCol = `Insurance${payerOrder}`;
        return insuranceBucketCol;
      }
      return null;
    }
    const statusObj = transactionStatusCodes.find(
      (ts: any) => ts.Description === transactionStatus
    );
    if (statusObj) {
      return statusObj.TransactionAmountColumn || null;
    }
    return null;
  };

  const getPayerMaster = async (
    accNumber: any,
    invNumber: any,
    legApp: any,
    legMRN: any,
    currentPaymentType: any
  ) => {
    try {
      setPayerMasterLoading(true);
      const tk = EncryptUtils.encryptedData({
        accountNumber: accNumber,
        invoiceNumber: invNumber,
        legacyApp: legApp,
        legacyMRN: legMRN,
        paymentType,
        paymentConfig: props?.paymentConfig
      });
      const payerMasterData = await APIService.getPayerMaster({tk});
      setPayerMasterLoading(false);
      setPayerMaster([...payerMasterData.data]);
      if (payerMasterData?.data?.length) {
        const payerMasterInfo = payerMasterData?.data[0];
        if (!selectedPayer) {
          setSelectedPayer(payerMasterInfo.ID);
        }
        getPayCodes(
          payerMasterInfo.ID,
          null,
          null,
          currentPaymentType || paymentType
        );
        getAdjustmentCodes(
          payerMasterInfo.ID,
          null,
          null,
          currentPaymentType || paymentType
        );
      }
    } catch (e) {
      setPayerMasterLoading(false);
      return;
    }
  };

  const getCollectionAgencies = async (currentPaymentType: any) => {
    try {
      setCollectionAgenciesLoading(true);
      const tk = EncryptUtils.encryptedData({
        legacyApp: props?.batchInfo?.LegacyApp,
        paymentConfig: props?.paymentConfig
      });
      const collectionAgenciesData = await APIService.getCollectionAgencies({
        tk
      });
      setCollectionAgenciesLoading(false);
      setCollectionAgencies([...collectionAgenciesData.data]);
      if (collectionAgenciesData?.data?.length) {
        const collectionAgencyInfo = collectionAgenciesData?.data[0];
        if (!selectedCollectionAgency) {
          setSelectedCollectionAgency(collectionAgencyInfo.ID);
        }
        getPayCodes(
          null,
          collectionAgencyInfo.ID,
          null,
          currentPaymentType || paymentType
        );
        getAdjustmentCodes(
          null,
          collectionAgencyInfo.ID,
          null,
          currentPaymentType || paymentType
        );
      }
    } catch (e) {
      setCollectionAgenciesLoading(false);
      return;
    }
  };

  const getSelfPayFinancialClasses = async (currentPaymentType: any) => {
    try {
      setFinancialClassLoading(true);
      const tk = EncryptUtils.encryptedData({
        legacyApp: props?.batchInfo?.LegacyApp,
        paymentConfig: props?.paymentConfig
      });
      const financialClassData = await APIService.getSelfPayFinancialClasses({
        tk
      });
      setFinancialClassLoading(false);
      setFinancialClasses([...financialClassData.data]);
      if (financialClassData?.data?.length) {
        const financialClassInfo = financialClassData?.data[0];
        setSelectedFinancialClass(financialClassInfo.ID);
        getPayCodes(
          null,
          null,
          financialClassInfo.ID,
          currentPaymentType || paymentType
        );
        getAdjustmentCodes(
          null,
          null,
          financialClassInfo.ID,
          currentPaymentType || paymentType
        );
      }
    } catch (e) {
      setFinancialClassLoading(false);
      return;
    }
  };

  const getInvoices = async (
    accountNumberValue: any,
    invoiceNumberValue: any,
    mrnValue: any,
    batchDetailInfo: any,
    isCobWindow = false
  ) => {
    setSearchLoading(true);
    const tk = EncryptUtils.encryptedData({
      invoiceNumber: invoiceNumberValue ? invoiceNumberValue.trim() : invoiceNumberValue,
      accountNumber: accountNumberValue ? accountNumberValue.trim() : accountNumberValue,
      MRN: mrnValue ? mrnValue.trim() : mrnValue,
      legacyApp: props?.batchInfo?.LegacyApp,
      batchId: props?.batchId,
      isCobWindow,
      batchDetailId: batchDetailInfo?.id || null,
      postingLevel: props?.postingLevel,
      paymentConfig: props?.paymentConfig
    });
    try {
      setServiceLinesLoading(true);
      let serviceLinesInfo: any = null;
      if (props?.postingLevel === 'INVOICE') {
        serviceLinesInfo = await APIService.getInvoices({tk});
      } else {
        serviceLinesInfo = await APIService.getServiceLines({tk});
      }
      const serviceLinesData = serviceLinesInfo?.data || [];
      const amountsData = serviceLinesInfo?.summaryAmountsData || [];
      const currentPaymentType = batchDetailInfo.DetailType || paymentType;
      if (serviceLinesData.length) {
        updatePatientInfo(serviceLinesData[0]);
        if (['COLLECTION_AGENCY'].includes(currentPaymentType)) {
          getCollectionAgencies(currentPaymentType);
        } else if (['PATIENT'].includes(currentPaymentType)) {
          getSelfPayFinancialClasses(currentPaymentType);
        } else if (['INSURANCE'].includes(currentPaymentType)) {
          getPayerMaster(
            batchDetailInfo?.AccountNumber || accountNumber,
            batchDetailInfo?.InvoiceNumber || invoiceNumber,
            props?.batchInfo?.LegacyApp,
            serviceLinesData[0].LegacyMRN || MRN,
            currentPaymentType
          );
        }
      } else {
        let errorMsg = '';
        if (props?.postingLevel === 'INVOICE') {
          errorMsg =
            'No Invoices found with active balance, Reset filters and try again.';
        } else {
          errorMsg =
            'No Service Lines found with active balance, Reset filters and try again.';
        }
        enqueueSnackbar(errorMsg, SnackBarConfig.getError());
      }
      if (isCobWindow) {
        setCobServiceLines(deepCopy([...serviceLinesData]));
        setCobSummaryAmounts(amountsData);
      } else {
        setServiceLinesRows(deepCopy([...serviceLinesData]));
        setServiceLinesRowsCopy(deepCopy([...serviceLinesData]));
        setSummaryAmounts(amountsData);
      }
      setSearchLoading(false);
      setServiceLinesLoading(false);
    } catch (e) {
      setSearchLoading(false);
      setServiceLinesLoading(false);
    }
  };

  const getBatchDetails = async (pageVal: any) => {
    const getBatchPayload = {
      batchId: props?.batchId,
      limit: 1,
      offset: (pageVal - 1) * 1,
      paymentConfig: props?.paymentConfig
    };
    try {
      setIsLoading(true);
      setPendingTransactions([]);
      setCobPendingTransactions([]);
      const tk = EncryptUtils.encryptedData(getBatchPayload);
      const batchDetailsInfo = await APIService.getBatchDetails({tk});
      const currentBatchDetails = batchDetailsInfo?.data.batches;
      if (currentBatchDetails.length) {
        setBatchDetail([...currentBatchDetails]);
        setInvoiceNumber(currentBatchDetails[0].InvoiceNumber);
        setAccountNumber(currentBatchDetails[0].AccountNumber);
        setMRN(currentBatchDetails[0].LegacyMRN);
        if (currentBatchDetails[0].TrackingDescription) {
          setTrackingDescription(currentBatchDetails[0].TrackingDescription);
          setDefaultTrackingDescription(
            currentBatchDetails[0].TrackingDescription
          );
        }
        setTotalPaymentPages(batchDetailsInfo.data.totalRecordsCount);
        setPaymentType(currentBatchDetails[0].DetailType);
        setTotalBatchAmount(currentBatchDetails[0].TotalBatchAmount);
        setTotalBatchPostedAmount(
          currentBatchDetails[0].TotalBatchAmountPosted
        );
        if (currentBatchDetails[0]?.PayerMasterID) {
          setSelectedPayer(currentBatchDetails[0]?.PayerMasterID);
        }
        if (currentBatchDetails[0]?.CollectionAgencyID) {
          setSelectedCollectionAgency(
            currentBatchDetails[0]?.CollectionAgencyID
          );
        }
        if (currentBatchDetails[0]?.PayCode === 'NONE') {
          setSelectedPayCode(currentBatchDetails[0]?.PayCode);
        } else if (currentBatchDetails[0]?.PayCodeMappingID) {
          setSelectedPayCode(currentBatchDetails[0]?.PayCodeMappingID);
        }
        if (currentBatchDetails[0]?.AdjustmentCode === 'NONE') {
          setSelectedAdjustmentCode(currentBatchDetails[0]?.AdjustmentCode);
        } else if (currentBatchDetails[0]?.AdjustmentCodeMappingID) {
          setSelectedAdjustmentCode(
            currentBatchDetails[0]?.AdjustmentCodeMappingID
          );
        }
        setTotalBatchVarianceAmount(currentBatchDetails[0].TotalVariance);
        setPageDisabled(true);
        setIsNewPayment(false);
        getInvoices(
          currentBatchDetails[0].AccountNumber,
          currentBatchDetails[0].InvoiceNumber,
          currentBatchDetails[0].LegacyMRN,
          currentBatchDetails[0],
          false
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const onRemoveBatchDetail = async () => {
    if (!batchDetail.length) {
      return;
    }
    const tk = EncryptUtils.encryptedData({
      batchId: props?.batchId,
      batchDetailId: getBatchDetailId(batchDetail),
      paymentConfig: props?.paymentConfig
    });
    try {
      setRemoveBatchDetailLoading(true);
      await APIService.removeBatchDetail({tk});
      onCloseRemoveBatchDetailDialog();
      resetState();
      setRemoveBatchDetailLoading(false);
      const pageVal = totalPaymentPages > 1 ? totalPaymentPages - 1 : 1;
      setPaymentPage(pageVal);
      setInputPage(pageVal);
      getBatchDetails(pageVal);
    } catch (e) {
      setRemoveBatchDetailLoading(false);
    }
  };

  const savePaymentEntryTransactions = async (
    isCobWindow = false,
    batchDetailCopy: any
  ) => {
    const serviceLineInfo = serviceLinesRows[0];
    const batchDetailId = getBatchDetailId(batchDetailCopy);
    const selectedPayerObj = getSelectedPayerObject();
    const selectedPayCodeObj = getSelectedPayCodeObject();
    const selectedAdjustmentCodeObj = getSelectedAdjustmentCodeObj();
    const selectedCollectionAgencyObj = getSelectedCollectionAgencyObj();
    const batchDetailInfo = {
      legacyApp: props?.batchInfo?.LegacyApp,
      batchId: props?.batchId,
      batchDetailId: batchDetailId,
      batchDetailAccountNumber: accountNumber,
      batchDetailInvoiceNumber: invoiceNumber,
      trackingDescription: trackingDescription,
      paymentType: paymentType,
      userEmail: userEmail,
      payerMasterId: selectedPayer,
      payCodeId: selectedPayCode,
      adjustmentCodeId: selectedAdjustmentCode,
      serviceLineRecord: serviceLineInfo,
      transactions: isCobWindow ? cobPendingTransactions : pendingTransactions,
      isCobWindow,
      postingLevel: props?.postingLevel,
      paymentConfig: props?.paymentConfig,
      payer: selectedPayerObj?.Name || null,
      payCode: selectedPayCodeObj?.PayCode || null,
      adjustmentCode: selectedAdjustmentCodeObj?.PayCode || null,
      payCodeMappingId: selectedPayCodeObj?.ID || null,
      adjustmentCodeMappingId: selectedAdjustmentCodeObj?.ID || null,
      collectionAgency: selectedCollectionAgencyObj?.Name || null,
      collectionAgencyId: selectedCollectionAgencyObj?.ID || null
    };
    if (
      !batchDetailInfo?.transactions?.length &&
      defaultTrackingDescription === trackingDescription
    ) {
      setPageDisabled(true);
      return;
    }
    try {
      setSaveButtonLoading(true);
      const tk = EncryptUtils.encryptedData(batchDetailInfo);
      let res: any = null;
      if (props?.postingLevel === 'INVOICE') {
        res = await APIService.postInvoiceTransactions({tk});
      } else {
        res = await APIService.postLineItemTransactions({tk});
      }
      if (!batchDetailId && res.batchDetailInfo && res.batchDetailInfo.length) {
        setBatchDetail([...res.batchDetailInfo]);
      }
      setSaveButtonLoading(false);
      if (isCobWindow) {
        setCobPendingTransactions([]);
      } else {
        setPendingTransactions([]);
      }
      if (!isCobWindow) {
        setPageDisabled(true);
        if (batchDetailId) {
          await getBatchDetails(paymentPage);
        } else if (totalPaymentPages) {
          setPaymentPage(totalPaymentPages + 1);
          setInputPage(totalPaymentPages + 1);
          setTotalPaymentPages(totalPaymentPages + 1);
          await getBatchDetails(totalPaymentPages + 1);
        } else {
          await getBatchDetails(paymentPage);
        }
      } else if (
        !batchDetailId &&
        res.batchDetailInfo &&
        res.batchDetailInfo.length
      ) {
        setBatchDetail([...res.batchDetailInfo]);
        setPaymentPage(totalPaymentPages + 1);
        setInputPage(totalPaymentPages + 1);
        setTotalPaymentPages(totalPaymentPages + 1);
      }
      return res.batchDetailInfo;
    } catch (e) {
      setSaveButtonLoading(false);
      return null;
    }
  };

  const getBytepadUsers = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig,
        applicationName: applicationName
      });
      const usersInfoData = await APIService.getBytepadUsers({tk});
      setUsersInfo([...usersInfoData.data]);
    } catch (e) {
      return;
    }
  };

  const clearPaymentEntryTransactions = async () => {
    const tk = EncryptUtils.encryptedData({
      batchId: props?.batchId,
      batchDetailId: getBatchDetailId(batchDetail),
      paymentConfig: props?.paymentConfig
    });
    try {
      setClearTransactionsLoading(true);
      await APIService.clearPaymentEntryTransactions({tk});
      setClearTransactionsLoading(false);
      setClearTransactionsDialog(false);
      setPendingTransactions([]);
      getInvoices(
        accountNumber,
        invoiceNumber,
        MRN,
        batchDetail.length ? batchDetail[0] : {},
        false
      );
      setPaymentAmount(null);
      setAdjustmentAmount(null);
      setServiceDate(null);
    } catch (e) {
      setClearTransactionsLoading(false);
    }
  };

  const getTotalAmountByField = (field: string) => {
    const amountValue = serviceLinesRows.reduce(
      (sum: number, row: any) => sum + (Number(row[field]) || 0),
      0
    );
    return amountValue;
  };

  const recalculatePaymentEntry = async () => {
    const totalPaymentAmount = getTotalAmountByField('Payment');
    const totalAdjustmentAmount = getTotalAmountByField('Adjustment');
    const totalCollectionAgencyPayment = getTotalAmountByField(
      'CollectionAgencyPayment'
    );
    setPaymentAmount(
      isCollectionAgencyPayment()
        ? totalCollectionAgencyPayment
        : totalPaymentAmount
    );
    setAdjustmentAmount(totalAdjustmentAmount);
    // For cob transactions.
    const newEntries = pendingTransactions.filter(
      (pt: any) => !pt.previousActiveId
    );
    setCobPendingTransactions([...newEntries]);
  };

  const onNewPayment = () => {
    resetState();
    setIsNewPayment(true);
    setPageDisabled(false);
    setTrackingDescription(
      trackingDescription || props?.batchInfo?.DefaultTracking
    );
    setDefaultTrackingDescription(
      trackingDescription || props?.batchInfo?.DefaultTracking
    );
  };

  const getAmountValue = (amtVal: any) => {
    if (amtVal === 0) {
      return amtVal;
    }
    return amtVal ?? '';
  };

  const resetPayerAndPayCodes = () => {
    setSelectedPayer('');
    setSelectedPayCode('');
    setSelectedAdjustmentCode('');
  };

  const handleNext = () => {
    setPaymentPage(paymentPage + 1);
    setInputPage(paymentPage + 1);
    if (props?.batchId) {
      resetPayerAndPayCodes();
      getBatchDetails(paymentPage + 1);
    }
  };

  const handleRight = () => {
    setPaymentPage(totalPaymentPages);
    setInputPage(totalPaymentPages);
    if (props?.batchId) {
      resetPayerAndPayCodes();
      getBatchDetails(totalPaymentPages);
    }
  };

  const handlePrevious = () => {
    setPaymentPage(paymentPage - 1);
    setInputPage(paymentPage - 1);
    if (props?.batchId) {
      resetPayerAndPayCodes();
      getBatchDetails(paymentPage - 1);
    }
  };

  const handleLeft = () => {
    setPaymentPage(1);
    setInputPage(1);
    if (props?.batchId) {
      resetPayerAndPayCodes();
      getBatchDetails(1);
    }
  };

  const handleSelectFieldChange = (name: string, value: string) => {
    switch (name) {
      case 'selectedPayer':
        setSelectedPayer(value);
        getPayCodes(value, null, null, paymentType);
        getAdjustmentCodes(value, null, null, paymentType);
        break;
      case 'selectedPayCode':
        setSelectedPayCode(value);
        break;
      case 'selectedAdjustmentCode':
        setSelectedAdjustmentCode(value);
        break;
      case 'selectedAgencyPayCode':
        setSelectedAgencyPayCode(value);
        break;
      case 'selectedCollectionAgency':
        setSelectedCollectionAgency(value);
        getPayCodes(null, value, null, paymentType);
        getAdjustmentCodes(null, value, null, paymentType);
        break;
      case 'selectedFinancialClass':
        setSelectedFinancialClass(value);
        getPayCodes(null, null, value, paymentType);
        getAdjustmentCodes(null, null, value, paymentType);
        break;
      default:
        break;
    }
  };

  const handlePaymentTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPaymentType = event.target.value;
    setSelectedPayer('');
    setSelectedPayCode('');
    setSelectedAdjustmentCode('');
    setPendingTransactions([]);
    setServiceLinesRows([...serviceLinesRowsCopy]);
    setSelectedCollectionAgency('');
    setSelectedFinancialClass('');
    setPaymentType(newPaymentType);
    if (newPaymentType === 'COLLECTION_AGENCY') {
      setPayerMaster([]);
      getCollectionAgencies(newPaymentType);
    } else if (newPaymentType === 'PATIENT') {
      setPayerMaster([]);
      getSelfPayFinancialClasses(newPaymentType);
    } else if (newPaymentType === 'INSURANCE') {
      setPayCodes([]);
      setAdjustmentCodes([]);
      getPayerMaster(
        accountNumber,
        invoiceNumber,
        props?.batchInfo?.LegacyApp,
        MRN,
        newPaymentType
      );
    }
  };

  const getGridUpdatedValue = (row: any, field: string) => {
    let pendingEntry: any = null;
    if (props?.postingLevel === 'INVOICE') {
      pendingEntry = currentTransactions.find(
        (t: any) => t.InvoiceNumber === row.InvoiceNumber && t.type === field
      );
    } else {
      pendingEntry = currentTransactions.find(
        (t: any) =>
          t.BillingTransaction_TransactionID === row.TransactionID &&
          t.type === field
      );
    }
    return pendingEntry ? pendingEntry.UpdatedValue : row[field];
  };

  const arraysAreDifferent = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return true;

    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.some((val, index) => val !== sortedB[index]);
  };

  const isServiceLineEditable = () => {
    if (displayCobWindow) return true;
    if (pageDisabled) return false;
    return true;
    // return selectedPayCode ? true : false;
  };

  const isRowSelectable = (params: any, gridDisplayType: string) => {
    return false;
  };

  const isCellEditable = (gridDisplayType: string, params: any) => {
    if (params.row.id === 'summary_record') {
      return false;
    }
    if (['service_lines', 'cob_window'].includes(gridDisplayType)) {
      return isServiceLineEditable();
    }
    return false;
  };

  const getPayerMasterOptions = () => {
    return payerMaster.map((f: any) => {
      return {
        label: f.Name,
        value: f.ID
      };
    });
  };

  const getCollectionAgencyOptions = () => {
    return collectionAgencies.map((f: any) => {
      return {
        label: f.Name,
        value: f.ID
      };
    });
  };

  const getFinancialClassOptions = () => {
    return financialClasses.map((f: any) => {
      return {
        label: f.Name,
        value: f.ID
      };
    });
  };

  const getPayCodeOptions = () => {
    const payCodeOptions = payCodes.map((f: any) => {
      return {
        label: f.PayCodeDescription
          ? `${f.PayCode} - ${f.PayCodeDescription}`
          : f.PayCode,
        value: f.ID
      };
    });
    payCodeOptions.push({
      label: 'NONE',
      value: 'NONE'
    });
    return payCodeOptions;
  };

  const getAdjustmentCodeOptions = () => {
    const adjustmentCodeOptions = adjustmentCodes.map((f: any) => {
      return {
        label: f.PayCodeDescription
          ? `${f.PayCode} - ${f.PayCodeDescription}`
          : f.PayCode,
        value: f.ID
      };
    });
    adjustmentCodeOptions.push({
      label: 'NONE',
      value: 'NONE'
    });
    return adjustmentCodeOptions;
  };

  const onRowSelectionModelChange = (e: any, gridDisplayType: string) => {
    return;
  };

  const getAggregatedBalance = (
    row: any,
    paymentValue: any,
    adjustmentValue: any
  ) => {
    let principalValue = null;
    if (row?.ChargeBalance !== null) {
      principalValue = row?.ChargeBalance;
    } else {
      principalValue = row?.ChargeAmount;
    }
    const transactionsSum = Number(row?.PaymentsTransactionsSum);
    const calculatedAmount =
      Number(principalValue) -
      (Number(transactionsSum) +
        Number(paymentValue) +
        Number(adjustmentValue));
    return calculatedAmount;
  };

  const applyCollectionOrPatientPayments = () => {
    setPendingTransactions((prevTransactions: any) => {
      const amountToApply = paymentAmount;
      const dateOfService = dayjs(serviceDate).format('YYYY-MM-DD');

      const selectedPayerObj = getSelectedPayerObject();
      const selectedPayCodeObj = getSelectedPayCodeObject();

      if (!Array.isArray(serviceLinesRows) || serviceLinesRows.length === 0) {
        return prevTransactions;
      }

      let dateOfServicesCopy = serviceLinesRows.filter(
        s => s.ServiceDateFormatted === dateOfService
      );

      if (
        !Array.isArray(dateOfServicesCopy) ||
        dateOfServicesCopy.length === 0
      ) {
        dateOfServicesCopy = [...serviceLinesRows];
      }

      let remainingAmount = amountToApply || 0;
      const paymentsToUpdate: any[] = [];

      for (const serviceLineRecord of dateOfServicesCopy) {
        if (remainingAmount <= 0) break;

        const {Balance} = serviceLineRecord;
        const amountToDeduct = Math.min(Balance, remainingAmount);

        if (paymentType === 'COLLECTION_AGENCY') {
          const collectionAgencyFee = 0;
          const collectionAgencyPayment = amountToDeduct;
          const newBalance = getAggregatedBalance(
            serviceLineRecord,
            amountToDeduct,
            null
          );

          paymentsToUpdate.push({
            ...serviceLineRecord,
            CollectionAgencyPayment: collectionAgencyPayment,
            CollectionAgencyFee: collectionAgencyFee,
            Balance: newBalance
          });
        } else if (paymentType === 'PATIENT') {
          const newBalance = getAggregatedBalance(
            serviceLineRecord,
            amountToDeduct,
            null
          );

          paymentsToUpdate.push({
            ...serviceLineRecord,
            Payment: amountToDeduct,
            Balance: newBalance
          });
        }

        remainingAmount -= amountToDeduct;
      }

      setServiceLinesRows((prevRows: any) => {
        const updatedRows = prevRows.map((row: any) => {
          let updatedRow: any = null;
          if (props?.postingLevel === 'INVOICE') {
            updatedRow = paymentsToUpdate.find(
              pRow => pRow.InvoiceNumber === row.InvoiceNumber
            );
          } else {
            updatedRow = paymentsToUpdate.find(
              pRow => pRow.TransactionID === row.TransactionID
            );
          }
          return updatedRow ? {...row, ...updatedRow} : row;
        });

        return [...updatedRows];
      });

      const updatedTransactions = [...prevTransactions];

      paymentsToUpdate.forEach(serviceLineRecord => {
        const masterTransactionRecord = {
          LegacyApp: serviceLineRecord.LegacyApp,
          LegacyMRN: serviceLineRecord.LegacyMRN,
          InvoiceNumber: serviceLineRecord.InvoiceNumber,
          AccountNumber: serviceLineRecord.AccountNumber,
          PaymentType: paymentType,
          PayerMasterID: selectedPayer,
          PayCodeMappingID: selectedPayCodeObj?.ID || null,
          PayCode: selectedPayCodeObj?.PayCode || null,
          PayCodeDescription: selectedPayCodeObj?.PayCodeDescription || null,
          FinancialClass: selectedPayerObj?.FinancialClassTypeName || null,
          FinancialClassTypeID: selectedPayerObj?.FinancialClassTypeID || null,
          CheckNumber: trackingDescription || null,
          ServiceDate: serviceLineRecord?.ServiceFromDate || null,
          TransactionStatus: serviceLineRecord?.TransactionStatus,
          BillingTransaction_TransactionID: null,
          TransactionParentID: null,
          TransactionID: null
        };
        if (props?.postingLevel === 'CHARGE') {
          masterTransactionRecord.BillingTransaction_TransactionID =
            serviceLineRecord?.TransactionID;
          masterTransactionRecord.TransactionParentID =
            serviceLineRecord?.TransactionParentID;
        }

        const transactionEntries = [
          {
            ...masterTransactionRecord,
            type:
              paymentType === 'COLLECTION_AGENCY'
                ? 'CollectionAgencyPayment'
                : 'Payment',
            UpdatedValue:
              paymentType === 'COLLECTION_AGENCY'
                ? serviceLineRecord.CollectionAgencyPayment
                : serviceLineRecord.Payment,
            previousActiveId:
              paymentType === 'COLLECTION_AGENCY'
                ? serviceLineRecord?.CollectionAgencyPaymentId
                : serviceLineRecord?.PaymentId ?? null
          }
        ];

        if (paymentType === 'COLLECTION_AGENCY') {
          transactionEntries.push({
            ...masterTransactionRecord,
            type: 'CollectionAgencyFee',
            UpdatedValue: serviceLineRecord.CollectionAgencyFee,
            previousActiveId: serviceLineRecord.CollectionAgencyFeeId ?? null
          });
        }

        transactionEntries.forEach(entry => {
          let existingIndex = -1;
          if (props?.postingLevel === 'INVOICE') {
            existingIndex = updatedTransactions.findIndex(
              t =>
                t.InvoiceNumber === entry.InvoiceNumber && t.type === entry.type
            );
          } else {
            existingIndex = updatedTransactions.findIndex(
              t =>
                t.BillingTransaction_TransactionID ===
                  entry?.BillingTransaction_TransactionID &&
                t.type === entry.type
            );
          }

          if (existingIndex !== -1) {
            updatedTransactions[existingIndex] = entry;
          } else {
            updatedTransactions.push(entry);
          }
        });
      });

      return updatedTransactions;
    });
  };

  const onApplyButtonClick = () => {
    applyCollectionOrPatientPayments();
  };

  const getCreatedByText = () => {
    return `${batchDetail[0].createdByUserName} ${moment.utc(batchDetail[0].BytepadCreatedDate).format('DD/MM/YYYY HH:mm')}`;
  };

  const getModifiedByText = () => {
    if (batchDetail[0].BytepadUpdatedDate && batchDetail[0].updatedByUserName) {
      return `${batchDetail[0].updatedByUserName} ${moment.utc(batchDetail[0].BytepadUpdatedDate).format('DD/MM/YYYY HH:mm')}`;
    }
  };

  const onClearPaymentEntry = () => {
    setClearTransactionsDialog(true);
  };

  const onCobWindowClick = () => {
    if (!isSavedBatchDetail()) {
      const pendingTransactionsCopy = deepCopy([...pendingTransactions]);
      const currentSummaryAmounts = deepCopy([...summaryAmounts]);
      const lineItemRows = deepCopy([...serviceLinesRows]);
      setCobPendingTransactions(pendingTransactionsCopy);
      setCobSummaryAmounts(currentSummaryAmounts);
      setCobServiceLines(lineItemRows);
    } else {
      getInvoices(
        accountNumber,
        invoiceNumber,
        MRN,
        batchDetail.length ? batchDetail[0] : {},
        true
      );
      // For cob transactions.
      const newEntries = pendingTransactions.filter(
        (pt: any) => !pt.previousActiveId
      );
      setCobPendingTransactions([...newEntries]);
    }
    setDisplayCobWindow(true);
  };

  const onCloseCobWindow = () => {
    setDisplayCobWindow(false);
    setCobServiceLines([]);
    setCobSummaryAmounts([]);
    setCobPendingTransactions([]);
  };

  const saveCobPaymentEntryTransactions = async (batchDetailCopy: any) => {
    await savePaymentEntryTransactions(true, batchDetailCopy);
    onCloseCobWindow();
  };

  const onSavePaymentEntry = async () => {
    const batchDetailCopy = await savePaymentEntryTransactions(
      false,
      batchDetail
    );
    if (cobPendingTransactions.length) {
      await saveCobPaymentEntryTransactions(batchDetailCopy);
    }
  };

  useEffect(() => {
    getBytepadUsers();
    getAgencyPayCodes();
    getDenialReasonCodes();
    getTransactionStatusCodes();
    getBatchDetails(paymentPage);
    setTotalBatchAmount(props.batchInfo?.TotalBatchAmount);
    setTotalBatchPostedAmount(props.batchInfo?.TotalBatchAmountPosted);
    setTotalBatchVarianceAmount(props.batchInfo?.TotalVariance);
  }, []);

  const selectFields = [
    paymentType === 'INSURANCE'
      ? {
          name: 'selectedPayer',
          placeholder: 'Select Payer',
          label: 'Payer',
          options: getPayerMasterOptions(),
          value: selectedPayer,
          disable: pageDisabled || isEmptyServiceLines() || payerMasterLoading
        }
      : paymentType === 'COLLECTION_AGENCY'
        ? {
            name: 'selectedCollectionAgency',
            placeholder: 'Select Payer',
            label: 'Payer',
            options: getCollectionAgencyOptions(),
            value: selectedCollectionAgency,
            disable:
              pageDisabled || isEmptyServiceLines() || collectionAgenciesLoading
          }
        : {
            name: 'selectedFinancialClass',
            placeholder: 'Select Payer',
            label: 'Payer',
            options: getFinancialClassOptions(),
            value: selectedFinancialClass,
            disable:
              pageDisabled || isEmptyServiceLines() || financialClassLoading
          },
    {
      name: 'selectedPayCode',
      placeholder: 'Select Pay Code',
      label: 'Pay Code',
      options: getPayCodeOptions(),
      value: selectedPayCode,
      disable:
        payCodeDisabled ||
        isEmptyServiceLines() ||
        pageDisabled ||
        payCodesLoading
    },
    {
      name: 'selectedAdjustmentCode',
      placeholder: 'Select Adjustment Code',
      label: 'Adjustment Code',
      options: getAdjustmentCodeOptions(),
      value: selectedAdjustmentCode,
      disable:
        adjustmentCodeDisabled ||
        isEmptyServiceLines() ||
        pageDisabled ||
        adjustmentCodesLoading
    }
  ];

  const getReasonCodeOptions = () => {
    return denialReasonCodes.map((s: any) => {
      return {
        label: s.Code,
        value: s.Code
      };
    });
  };

  const getPaymentColNameBasedOnPaymentType = (rowItem: any) => {
    if (
      paymentType === 'COLLECTION_AGENCY' &&
      rowItem?.CollectionAgencyPayment !== null
    ) {
      return {
        colName: 'CollectionAgencyPayment',
        colId: 'CollectionAgencyPaymentId'
      };
    } else if (rowItem?.Payment !== null) {
      return {colName: 'Payment', colId: 'PaymentId'};
    } else if (rowItem?.Adjustment !== null) {
      return {colName: 'Adjustment', colId: 'AdjustmentId'};
    } else if (paymentType === 'COLLECTION_AGENCY') {
      return {
        colName: 'CollectionAgencyPayment',
        colId: 'CollectionAgencyPaymentId'
      };
    } else {
      return {colName: 'Payment', colId: 'PaymentId'};
    }
  };

  const getReasonCodeDescription = (reasonCodesValue: any) => {
    let codesDescriptions = '';
    const reasonCodesList =
      typeof reasonCodesValue === 'string'
        ? reasonCodesValue.split(',').map(v => v.trim())
        : reasonCodesValue || [];
    if (reasonCodesList && reasonCodesList.length) {
      reasonCodesList.map((code: any) => {
        const reason = denialReasonCodes.find((s: any) => s.Code === code);
        codesDescriptions +=
          reason && reason.Code ? `${code} - ${reason.Description} ` : '';
      });
    }
    return codesDescriptions.trim();
  };

  const getTransactionStatusCodeOptions = () => {
    let statusCodes = transactionStatusCodes.map((s: any) => {
      return {
        label: s.Description,
        value: s.Description
      };
    });
    if (['COLLECTION_AGENCY', 'PATIENT'].includes(paymentType)) {
      statusCodes = statusCodes.filter(
        (sc: any) => sc.value === 'Settle moved to Patient'
      );
    }
    return statusCodes;
  };

  const getTransactionStatusCodeDesc = (transactionStatusId: any) => {
    if (!transactionStatusId) return '';
    return transactionStatusCodes.find((s: any) => s.ID === transactionStatusId)
      .Description;
  };

  const renderEditAmountCell = (params: any) => {
    const updatedValue = getGridUpdatedValue(params.row, params.field);
    return shouldDisplayEditIcon ? (
      <FlexGridCell>
        <GridCellRender value={fCurrency(updatedValue)} />
        <IconButton
          disabled={!isServiceLineEditable()}
          sx={{color: 'success.main'}}
          size="small"
          onClick={() =>
            params.api.startCellEditMode({id: params.id, field: params.field})
          }>
          <EditIcon fontSize="small" />
        </IconButton>
      </FlexGridCell>
    ) : (
      <GridCellRender value={fCurrency(updatedValue)} />
    );
  };

  const isSummaryRecord = (params: any) => {
    return params.row.id === 'summary_record' ? true : false;
  };

  const getBoldText = (text: any) => {
    return <Typography sx={{fontWeight: 'bold'}}>{text}</Typography>;
  };

  const calculateColumnTotal = (field: string) => {
    return currentServiceLines.reduce(
      (sum: any, row: any) =>
        sum + (Number(getGridUpdatedValue(row, field)) || 0),
      0
    );
  };

  const renderCellValue = (params: any) => {
    const monetaryEditFields = [
      'Allowed',
      'Adjustment',
      'Interest',
      'Payment',
      'Deductible',
      'CollectionAgencyPayment',
      'CollectionAgencyFee'
    ];
    const monetaryNonEditFields = [
      'Balance',
      'ChargeAmount',
      'ChargeBalance',
      'CurrentInvoiceBalance',
      'Insurance1',
      'Insurance2',
      'Insurance3',
      'PatientResponsibility'
    ];
    const currentValue = params.value;
    const isSummaryRow = isSummaryRecord(params);
    const serialNo = params.api.getAllRowIds().indexOf(params.id) + 1;
    if (params.field === 's_no') {
      const totalText = getBoldText('Total');
      return <GridCellRender value={isSummaryRow ? totalText : serialNo} />;
    } else if (['TransactionStatus'].includes(params.field)) {
      const updatedValue = getGridUpdatedValue(params.row, params.field);
      return <GridCellRender value={updatedValue} />;
    } else if (params.field === 'ReasonCode') {
      const reasonCodesValue = getGridUpdatedValue(params.row, 'ReasonCode');
      return <GridCellRender value={reasonCodesValue} />;
    } else if (params.field === 'ReasonCodeDescription') {
      const reasonCodesValue = getGridUpdatedValue(params.row, 'ReasonCode');
      const reasonCodeDesc = getReasonCodeDescription(reasonCodesValue);
      return <GridCellRender value={reasonCodeDesc} />;
    } else if (
      ['ServiceDate', 'ServiceFromDate', 'ServiceThroughDate'].includes(
        params.field
      )
    ) {
      const dateValue = currentValue
        ? moment.utc(currentValue).format('MM/DD/YYYY')
        : null;
      return <GridCellRender value={dateValue} />;
    } else if (monetaryEditFields.includes(params.field)) {
      const sumValue = calculateColumnTotal(params.field);
      const boldAmount = getBoldText(fCurrency(sumValue));
      return isSummaryRow ? boldAmount : renderEditAmountCell(params);
    } else if (monetaryNonEditFields.includes(params.field)) {
      const sumValue = calculateColumnTotal(params.field);
      const boldAmount = getBoldText(fCurrency(sumValue));
      const updatedValue = getGridUpdatedValue(params.row, params.field);
      return isSummaryRow ? (
        boldAmount
      ) : (
        <GridCellRender value={fCurrency(updatedValue)} />
      );
    }
    return <GridCellRender value={currentValue} />;
  };

  const processRowUpdate = (
    updatedRow: any,
    originalRow: any,
    gridDisplayType: any
  ) => {
    let newRow = {...updatedRow};
    const oldRow = {...originalRow};
    let updatedServiceLinesRows = deepCopy([...currentServiceLines]);

    const {colName, colId} = getPaymentColNameBasedOnPaymentType(newRow);
    const paymentColName = colName;
    const paymentColId = colId;

    let hasChange = false;
    let updatedEntries: any[] = [];

    const masterInsuranceAmounts = [
      'Insurance1',
      'Insurance2',
      'Insurance3',
      'PatientResponsibility'
    ];

    const amountFields = [
      {key: 'Allowed', idKey: 'AllowedId'},
      {key: 'Adjustment', idKey: 'AdjustmentId'},
      {key: 'Payment', idKey: 'PaymentId'},
      {key: 'Deductible', idKey: 'DeductibleId'},
      {key: 'Interest', idKey: 'InterestId'},
      {key: 'CollectionAgencyPayment', idKey: 'CollectionAgencyPaymentId'},
      {key: 'CollectionAgencyFee', idKey: 'CollectionAgencyFeeId'},
      {key: 'Balance', idKey: 'BalanceId'}
    ];

    const selectedPayerObj = getSelectedPayerObject();
    const selectedPayCodeObj = getSelectedPayCodeObject();
    const selectedAdjustmentCodeObj = getSelectedAdjustmentCodeObj();
    const selectedCollectionAgencyObj = getSelectedCollectionAgencyObj();

    const masterTransactionRecord = {
      LegacyApp: newRow.LegacyApp,
      LegacyMRN: newRow.LegacyMRN,
      AccountNumber: newRow.AccountNumber,
      InvoiceNumber: newRow.InvoiceNumber,
      PaymentType: paymentType,
      PayerMasterID: selectedPayer,
      PayCodeMappingID: selectedPayCodeObj?.ID || null,
      PayCode: selectedPayCodeObj?.PayCode || null,
      PayCodeDescription: selectedPayCodeObj?.PayCodeDescription || null,
      AdjustmentPayCodeMappingID: selectedAdjustmentCodeObj?.ID,
      AdjustmentCode: selectedAdjustmentCodeObj?.PayCode || null,
      AdjustmentCodeDescription:
        selectedAdjustmentCodeObj?.PayCodeDescription || null,
      FinancialClass: selectedPayerObj?.FinancialClassTypeName || null,
      FinancialClassTypeID: selectedPayerObj?.FinancialClassTypeID || null,
      CollectionAgency: selectedCollectionAgencyObj?.Name || null,
      CollectionAgencyID: selectedCollectionAgencyObj?.ID || null,
      // Tracking description contains check number as per requirements
      CheckNumber: trackingDescription || null,
      ServiceDate: newRow?.ServiceFromDate || newRow?.ServiceDate || null,
      BillingTransaction_TransactionID: null,
      TransactionParentID: null,
      ReasonCode: newRow?.ReasonCode || null,
      ReasonCodeDescription: newRow?.ReasonCodeDescription || null,
      Insurance1: newRow?.Insurance1 || null,
      Insurance2: newRow?.Insurance2 || null,
      Insurance3: newRow?.Insurance3 || null,
      PatientResponsibility: newRow?.PatientResponsibility || null
    };
    if (props?.postingLevel === 'CHARGE') {
      masterTransactionRecord.BillingTransaction_TransactionID =
        newRow?.TransactionID;
      masterTransactionRecord.TransactionParentID = newRow?.TransactionParentID;
    }

    const currentLineItemIndex = updatedServiceLinesRows.findIndex(
      (es: any) => es.id === newRow.id
    );

    const insuranceAmountSetter = (
      entriesCopyMain: any,
      updatedBalance: any,
      rowCopyMain: any,
      serviceLinesRowsCopyMain: any,
      currentServiceLineIndex: any
    ) => {
      const entriesCopy = [...entriesCopyMain];
      const serviceLinesRowsCopy2 = [...serviceLinesRowsCopyMain];
      const rowCopy = {...rowCopyMain};
      if (Number(updatedBalance) !== null) {
        const insuranceColumn = getInsuranceColumnBasedOnTransactionStatus(
          rowCopy.TransactionStatus
        );
        if (insuranceColumn) {
          const existingIndex = entriesCopy.findIndex((entry: any) =>
            [paymentColName].includes(entry?.type)
          );
          const insuranceNullObj: Record<string, any> = {};
          const otherInsuranceColumns = masterInsuranceAmounts.filter(
            f => f !== insuranceColumn
          );
          otherInsuranceColumns.forEach((o: any) => {
            rowCopy[o] = null;
            insuranceNullObj[o] = null;
            serviceLinesRowsCopy2[currentServiceLineIndex][o] = null;
          });
          if (existingIndex !== -1) {
            entriesCopy[existingIndex] = {
              ...entriesCopy[existingIndex],
              [insuranceColumn]: Number(updatedBalance),
              ...insuranceNullObj
            };
            rowCopy[insuranceColumn] = Number(updatedBalance);
            serviceLinesRowsCopy2[currentServiceLineIndex][insuranceColumn] =
              Number(updatedBalance);
          } else {
            const currentPaymentValue = getGridUpdatedValue(
              rowCopy,
              paymentColName
            );
            entriesCopy.push({
              ...masterTransactionRecord,
              TransactionStatus: rowCopy.TransactionStatus,
              type: paymentColName,
              UpdatedValue: currentPaymentValue,
              previousActiveId: rowCopy[paymentColId] ?? null,
              [insuranceColumn]: Number(updatedBalance),
              ...insuranceNullObj
            });
            rowCopy[insuranceColumn] = Number(updatedBalance);
            serviceLinesRowsCopy2[currentServiceLineIndex][insuranceColumn] =
              Number(updatedBalance);
          }
        }
      }
      return {entriesCopy, rowCopy, serviceLinesRowsCopy2};
    };

    if (currentLineItemIndex === -1) return newRow; // If row not found, return newRow

    amountFields.forEach(({key, idKey}) => {
      if (newRow[key] !== oldRow[key]) {
        const updatedValueValue =
          newRow[key] !== null ? Number(newRow[key]) : null;

        // Update the local copy of the service line
        updatedServiceLinesRows[currentLineItemIndex][key] = updatedValueValue;

        // Add to pending transactions
        const newUpdatedEntry = {
          ...masterTransactionRecord,
          type: key,
          UpdatedValue: updatedValueValue,
          previousActiveId: newRow[idKey] ?? null
        };
        let entryToUpdate = {};
        if (key === 'Adjustment') {
          entryToUpdate = {
            AdjustmentPayCodeMappingID: selectedAdjustmentCodeObj?.ID,
            AdjustmentCode: selectedAdjustmentCodeObj?.PayCode || null,
            AdjustmentCodeDescription:
              selectedAdjustmentCodeObj?.PayCodeDescription || null
          };
        }
        if (key === 'Payment') {
          entryToUpdate = {
            TransactionStatus: newRow?.TransactionStatus
          };
        }
        if (['Deductible', 'Interest'].includes(key)) {
          delete newUpdatedEntry.ReasonCode;
          delete newUpdatedEntry.ReasonCodeDescription;
          delete newUpdatedEntry.Insurance1;
          delete newUpdatedEntry.Insurance2;
          delete newUpdatedEntry.Insurance3;
          delete newUpdatedEntry.PatientResponsibility;
        }
        updatedEntries.push({...newUpdatedEntry, ...entryToUpdate});

        hasChange = true;
      }
    });

    if (newRow.Allowed !== oldRow.Allowed) {
      const principalCharge = newRow.ChargeBalance || newRow.ChargeAmount;
      const newAdjustment = Number(principalCharge) - Number(newRow.Allowed);
      newRow.Adjustment = newAdjustment;

      updatedServiceLinesRows[currentLineItemIndex]['Adjustment'] =
        newAdjustment;

      updatedEntries.push({
        ...masterTransactionRecord,
        AdjustmentPayCodeMappingID: selectedAdjustmentCodeObj?.ID,
        AdjustmentCode: selectedAdjustmentCodeObj?.PayCode || null,
        AdjustmentCodeDescription:
          selectedAdjustmentCodeObj?.PayCodeDescription || null,
        type: 'Adjustment',
        UpdatedValue: newAdjustment,
        previousActiveId: newRow.AdjustmentId ?? null
      });

      hasChange = true;

      // **Auto-calculate Balance if Payment exists**
      const currentPayment = getGridUpdatedValue(newRow, 'Payment');
      if (currentPayment !== null) {
        const newBalance = getAggregatedBalance(
          newRow,
          currentPayment,
          newRow?.Adjustment
        );
        newRow.Balance = newBalance;
        updatedServiceLinesRows[currentLineItemIndex]['Balance'] = newBalance;
        const insAmtResult = insuranceAmountSetter(
          deepCopy(updatedEntries),
          newBalance,
          deepCopy(newRow),
          deepCopy(updatedServiceLinesRows),
          currentLineItemIndex
        );
        updatedEntries = insAmtResult?.entriesCopy;
        newRow = insAmtResult?.rowCopy;
        updatedServiceLinesRows = insAmtResult?.serviceLinesRowsCopy2;

        hasChange = true;
      }
    }

    if (newRow.Payment !== oldRow.Payment) {
      const newBalance = getAggregatedBalance(
        newRow,
        newRow?.Payment,
        newRow?.Adjustment
      );
      newRow.Balance = newBalance;

      updatedServiceLinesRows[currentLineItemIndex]['Balance'] = newBalance;

      const insAmtResult = insuranceAmountSetter(
        deepCopy(updatedEntries),
        newBalance,
        deepCopy(newRow),
        deepCopy(updatedServiceLinesRows),
        currentLineItemIndex
      );
      updatedEntries = insAmtResult?.entriesCopy;
      newRow = insAmtResult?.rowCopy;
      updatedServiceLinesRows = insAmtResult?.serviceLinesRowsCopy2;

      hasChange = true;
    }

    if (newRow.Adjustment !== oldRow.Adjustment) {
      // const currentAllowed = getGridUpdatedValue(newRow, 'Allowed');
      const currentPayment = getGridUpdatedValue(newRow, 'Payment');
      const currentAdjustment = getGridUpdatedValue(newRow, 'Adjustment');
      const newBalance = getAggregatedBalance(
        newRow,
        currentPayment,
        currentAdjustment
      );
      newRow.Balance = newBalance;
      updatedServiceLinesRows[currentLineItemIndex]['Balance'] = newBalance;
      const insAmtResult = insuranceAmountSetter(
        deepCopy(updatedEntries),
        newBalance,
        deepCopy(newRow),
        deepCopy(updatedServiceLinesRows),
        currentLineItemIndex
      );
      updatedEntries = insAmtResult?.entriesCopy;
      newRow = insAmtResult?.rowCopy;
      updatedServiceLinesRows = insAmtResult?.serviceLinesRowsCopy2;
      hasChange = true;
    }

    if (newRow.CollectionAgencyPayment !== oldRow.CollectionAgencyPayment) {
      const newBalance = getAggregatedBalance(
        newRow,
        newRow.CollectionAgencyPayment,
        null
      );
      newRow.Balance = newBalance;
      updatedServiceLinesRows[currentLineItemIndex]['Balance'] = newBalance;

      const insAmtResult = insuranceAmountSetter(
        deepCopy(updatedEntries),
        newBalance,
        deepCopy(newRow),
        deepCopy(updatedServiceLinesRows),
        currentLineItemIndex
      );
      updatedEntries = insAmtResult?.entriesCopy;
      newRow = insAmtResult?.rowCopy;
      updatedServiceLinesRows = insAmtResult?.serviceLinesRowsCopy2;

      hasChange = true;
    }

    const reasonCodesNew = Array.isArray(newRow.ReasonCode)
      ? newRow.ReasonCode
      : (newRow.ReasonCode || '').split(',').map((s: any) => s.trim());

    const reasonCodesOld = Array.isArray(oldRow.ReasonCode)
      ? oldRow.ReasonCode
      : (oldRow.ReasonCode || '').split(',').map((s: any) => s.trim());

    if (arraysAreDifferent(reasonCodesNew, reasonCodesOld)) {
      const reasonCodeStrVal = reasonCodesNew.join(',');
      const existingIndex = updatedEntries.findIndex((entry: any) =>
        [paymentColName].includes(entry?.type)
      );
      if (existingIndex !== -1) {
        updatedEntries[existingIndex] = {
          ...updatedEntries[existingIndex],
          ReasonCode: reasonCodeStrVal,
          ReasonCodeDescription: getReasonCodeDescription(reasonCodeStrVal),
          StatusCode: 'DENIAL'
        };
        newRow.ReasonCode = reasonCodeStrVal;
        newRow.ReasonCodeDescription =
          getReasonCodeDescription(reasonCodeStrVal);
        updatedServiceLinesRows[currentLineItemIndex]['ReasonCode'] =
          reasonCodeStrVal;
        updatedServiceLinesRows[currentLineItemIndex]['ReasonCodeDescription'] =
          getReasonCodeDescription(reasonCodeStrVal);
      } else {
        const currentPaymentValue = getGridUpdatedValue(newRow, paymentColName);
        updatedEntries.push({
          ...masterTransactionRecord,
          TransactionStatus: newRow.TransactionStatus,
          type: paymentColName,
          UpdatedValue: currentPaymentValue,
          previousActiveId: newRow[paymentColId] ?? null,
          ReasonCode: reasonCodeStrVal,
          ReasonCodeDescription: getReasonCodeDescription(reasonCodeStrVal),
          StatusCode: 'DENIAL'
        });
        newRow.ReasonCode = reasonCodeStrVal;
        newRow.ReasonCodeDescription =
          getReasonCodeDescription(reasonCodeStrVal);

        updatedServiceLinesRows[currentLineItemIndex]['ReasonCode'] =
          reasonCodeStrVal;
        updatedServiceLinesRows[currentLineItemIndex]['ReasonCodeDescription'] =
          getReasonCodeDescription(reasonCodeStrVal);
      }
      hasChange = true;
    }

    if (newRow.TransactionStatus !== oldRow.TransactionStatus) {
      const existingIndex = updatedEntries.findIndex((entry: any) =>
        [paymentColName].includes(entry?.type)
      );
      if (existingIndex !== -1) {
        updatedEntries[existingIndex] = {
          ...updatedEntries[existingIndex],
          TransactionStatus: newRow.TransactionStatus
        };
      } else {
        const currentPaymentValue = getGridUpdatedValue(newRow, paymentColName);
        updatedEntries.push({
          ...masterTransactionRecord,
          TransactionStatus: newRow.TransactionStatus,
          type: paymentColName,
          UpdatedValue: currentPaymentValue,
          previousActiveId: newRow[paymentColId] ?? null
        });
      }

      updatedServiceLinesRows[currentLineItemIndex]['TransactionStatus'] =
        newRow.TransactionStatus;
      const currentBalance = getGridUpdatedValue(newRow, 'Balance');
      const insAmtResult = insuranceAmountSetter(
        deepCopy(updatedEntries),
        currentBalance,
        deepCopy(newRow),
        deepCopy(updatedServiceLinesRows),
        currentLineItemIndex
      );
      updatedEntries = insAmtResult?.entriesCopy;
      newRow = insAmtResult?.rowCopy;
      updatedServiceLinesRows = insAmtResult?.serviceLinesRowsCopy2;
      hasChange = true;
    }

    if (hasChange) {
      serviceLinesSetter([...updatedServiceLinesRows]);
    }

    transactionsSetter((prevTransactions: any) => {
      const updatedTransactions = [...prevTransactions];

      updatedEntries.forEach((entry: any) => {
        let existingIndex = -1;
        if (props?.postingLevel === 'INVOICE') {
          existingIndex = updatedTransactions.findIndex(
            t =>
              t.InvoiceNumber === entry.InvoiceNumber && t.type === entry.type
          );
        } else {
          existingIndex = updatedTransactions.findIndex(
            t =>
              t.BillingTransaction_TransactionID ===
                entry.BillingTransaction_TransactionID && t.type === entry.type
          );
        }

        if (existingIndex !== -1) {
          updatedTransactions[existingIndex] = entry;
        } else {
          updatedTransactions.push(entry);
        }
      });

      return [...updatedTransactions];
    });

    return {...newRow};
  };

  let serviceLinesColumns: GridColDef[] = [
    props?.postingLevel === 'INVOICE'
      ? {
          field: 'LegacyMRN',
          headerName: 'Legacy MRN',
          type: 'string',
          flex: 0,
          width: 100,
          renderCell: renderCellValue
        }
      : {
          field: 's_no',
          headerName: 'S.No',
          type: 'number',
          sortable: false,
          filterable: false,
          flex: 0,
          width: 70,
          headerAlign: 'left',
          align: 'left',
          renderCell: renderCellValue
        },
    props?.postingLevel === 'INVOICE'
      ? {
          field: 'AccountNumber',
          headerName: 'Account Number',
          type: 'string',
          flex: 0,
          width: 100,
          renderCell: renderCellValue
        }
      : {
          field: 'ServiceDate',
          headerName: 'Service Date',
          type: 'date',
          flex: 0,
          width: 100,
          valueGetter: (params: any) =>
            params.value ? moment.utc(params.value).toDate() : null,
          renderCell: renderCellValue
        },
    {
      field: 'InvoiceNumber',
      headerName: 'Invoice Number',
      type: 'string',
      flex: 0,
      width: 150,
      renderCell: renderCellValue
    },
    props?.postingLevel === 'INVOICE'
      ? {
          field: 'ServiceFromDate',
          headerName: 'Service From',
          type: 'date',
          flex: 0,
          width: 100,
          valueGetter: (params: any) =>
            params.value ? moment.utc(params.value).toDate() : null,
          renderCell: renderCellValue
        }
      : {
          field: 'CptCode',
          headerName: 'CPT4',
          type: 'string',
          flex: 0,
          width: 100,
          renderCell: renderCellValue
        },
    props?.postingLevel === 'INVOICE'
      ? {
          field: 'ServiceThroughDate',
          headerName: 'Service Through',
          type: 'date',
          flex: 0,
          width: 100,
          valueGetter: (params: any) =>
            params.value ? moment.utc(params.value).toDate() : null,
          renderCell: renderCellValue
        }
      : {
          field: 'RenderingPhysician',
          headerName: 'Rendering Provider',
          type: 'string',
          flex: 0,
          width: 150,
          renderCell: renderCellValue
        },
    {
      field: 'AgentCode',
      headerName: 'Agency Code',
      flex: 0,
      width: 100,
      editable: false,
      type: 'text',
      renderCell: renderCellValue
    },
    {
      field: 'ChargeAmount',
      headerName: 'Charge',
      flex: 0,
      width: 150,
      editable: false,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'Allowed',
      headerName: 'Allowed Amount',
      flex: 0,
      width: 150,
      editable: true,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'Interest',
      headerName: 'Interest Amount',
      flex: 0,
      width: 150,
      editable: true,
      cellClassName: 'autopost-grid-editable-cell',
      type: 'number',
      renderCell: renderCellValue
    },
    isCollectionAgencyPayment()
      ? {
          field: 'CollectionAgencyFee',
          headerName: 'Collection Agency Fee',
          flex: 0,
          width: 160,
          editable: true,
          type: 'number',
          renderCell: renderCellValue
        }
      : {
          field: 'Adjustment',
          headerName: 'Adjustment Amount',
          flex: 0,
          width: 150,
          editable: true,
          type: 'number',
          renderCell: renderCellValue
        },
    isCollectionAgencyPayment()
      ? {
          field: 'CollectionAgencyPayment',
          headerName: 'Collection Agency Payment',
          flex: 0,
          width: 180,
          editable: true,
          type: 'number',
          renderCell: renderCellValue
        }
      : {
          field: 'Payment',
          headerName: 'Payment Amount',
          flex: 0,
          width: 150,
          editable: true,
          type: 'number',
          renderCell: renderCellValue
        },
    {
      field: 'Balance',
      headerName: 'Balance Amount',
      flex: 0,
      width: 150,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'TransactionStatus',
      headerName: 'Transaction Status',
      flex: 0,
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: getTransactionStatusCodeOptions(),
      renderCell: renderCellValue
    },
    // {
    //   field: 'ReasonCode',
    //   headerName: 'Reason Code',
    //   flex: 0,
    //   width: 200,
    //   editable: true,
    //   type: 'singleSelect',
    //   valueOptions: getReasonCodeOptions(),
    //   renderCell: renderCellValue
    // },
    {
      field: 'ReasonCode',
      headerName: 'Reason Codes',
      flex: 0,
      width: 200,
      editable: true,
      type: 'string',
      renderCell: renderCellValue,
      renderEditCell: (params: any) => (
        <MultiSelectEditCell params={params} options={getReasonCodeOptions()} />
      )
    },
    {
      field: 'ReasonCodeDescription',
      headerName: 'Reason Code Description',
      flex: 0,
      width: 200,
      editable: false,
      type: 'string',
      renderCell: renderCellValue
    },
    {
      field: 'Deductible',
      headerName: 'Deductible Amount',
      flex: 0,
      width: 150,
      editable: true,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'Insurance1',
      headerName: 'Insurance1 Amount',
      flex: 0,
      width: 150,
      editable: false,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'Insurance2',
      headerName: 'Insurance2 Amount',
      flex: 0,
      width: 150,
      editable: false,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'Insurance3',
      headerName: 'Insurance3 Amount',
      flex: 0,
      width: 150,
      editable: false,
      type: 'number',
      renderCell: renderCellValue
    },
    {
      field: 'PatientResponsibility',
      headerName: 'Patient Responsibility Amount',
      flex: 0,
      width: 150,
      editable: false,
      type: 'number',
      renderCell: renderCellValue
    }
  ];

  // if (isPatientPayment()) {
  //   serviceLinesColumns = serviceLinesColumns.filter(
  //     s => s.field !== 'Adjustment'
  //   );
  // }
  serviceLinesColumns = serviceLinesColumns.map((sl: any) => {
    return {
      ...sl,
      align: 'left',
      headerAlign: 'left'
    };
  });

  const getDataGridProps = (gridDisplayType: string) => {
    let gridColumns, gridRows, gridLoading;
    if (gridDisplayType === 'service_lines') {
      gridColumns = serviceLinesColumns;
      gridRows = [...serviceLinesRows];
      gridLoading = serviceLinesLoading;
    } else if (gridDisplayType === 'cob_window') {
      gridColumns = serviceLinesColumns;
      gridRows = [...cobServiceLines];
      gridLoading = serviceLinesLoading;
    }
    const gridPropsObj = {
      gridPaginationStatus: false,
      gridHideFooterStatus: true,
      gridColumns: gridColumns || [],
      gridRows: gridRows || [],
      gridLoading: gridLoading || false,
      gridCheckboxSelection: false,
      horizontalScroll: true
    };
    return gridPropsObj;
  };

  const renderDataGridComponent = (gridDisplayType: string) => {
    const {
      gridPaginationStatus,
      gridHideFooterStatus,
      gridColumns,
      gridRows,
      gridLoading,
      gridCheckboxSelection,
      horizontalScroll
    } = getDataGridProps(gridDisplayType);

    let gridSummaryRows = summaryAmounts;
    if (gridDisplayType === 'cob_window') {
      gridSummaryRows = cobSummaryAmounts;
    }

    return (
      <Box
        px={3}
        sx={{
          marginTop: '1rem',
          marginBottom: '1rem',
          maxHeight: 'auto'
        }}>
        <StripedDataGrid
          showColumnVerticalBorder
          showCellVerticalBorder
          autoHeight
          disableRowGrouping
          disableRowSelectionOnClick
          disableColumnSelector
          disableAggregation
          checkboxSelection={gridCheckboxSelection}
          apiRef={gridApiRef}
          loading={gridLoading}
          columns={gridColumns}
          rows={[...gridRows]}
          rowThreshold={0}
          rowHeight={37}
          columnHeaderHeight={42}
          hideFooter={gridHideFooterStatus}
          pagination={gridPaginationStatus}
          paginationMode="server"
          sortingMode="client"
          sortingOrder={['desc', 'asc']}
          filterMode="client"
          pinnedRows={{bottom: gridRows.length ? gridSummaryRows : []}}
          // onSortModelChange={onSortModelChange}
          // onFilterModelChange={onFilterModelChange}
          processRowUpdate={(newRow: any, oldRow: any) =>
            processRowUpdate(newRow, oldRow, gridDisplayType)
          }
          // getCellClassName={getCellClassName}
          isCellEditable={(params: any) =>
            isCellEditable(gridDisplayType, params)
          }
          isRowSelectable={(e: any) => isRowSelectable(e, gridDisplayType)}
          onRowSelectionModelChange={(e: any) =>
            onRowSelectionModelChange(e, gridDisplayType)
          }
          getRowId={(row: any) => row.id || row.ID || row.BatchId}
          onProcessRowUpdateError={(error: any) => {
            return true;
          }}
          slotProps={{
            filterPanel: {
              sx: {'& .MuiDataGrid-filterFormValueInput': {width: 'auto'}}
            }
          }}
          getRowClassName={(params: any) => {
            return rowStyles(params);
          }}
          sx={{
            backgroundColor: `${APP.PALETTE.COMMON.WHITE}`,
            '& .MuiTablePagination-displayedRows': {
              display: 'none'
            },
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap'
            },
            '& .MuiDataGrid-row': {
              border: '1px solid lightgray',
              borderTopWidth: '0px',
              width: 'calc(100% - 2px)'
            },
            '& .MuiDataGrid-columnHeaders': {
              border: '1px solid lightgray',
              backgroundColor: `${theme.palette.grey[300]}`,
              borderBottomColor: `${theme.palette.primary.lighter}`,
              borderBottomWidth: '1px'
            },
            '& .MuiDataGrid-columnHeader--moving': {
              backgroundColor: `${APP.PALETTE.DATA_GRID.HEADER}`
            },
            '& .AbnormalFlagText': {
              color: `${APP.PALETTE.ABNORMAL_FLAG.TEXT}`,
              fontWeight: 'bold !important'
            },
            '& .css-87hgir-MuiTypography-root': {
              fontWeight: 'unset'
            },
            '& .css-11d09j8-MuiButtonBase-root-MuiCheckbox-root.Mui-checked': {
              color: props?.checkboxColor || `${theme.palette.primary.lighter}`
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: '70px',
              scrollBehavior: 'smooth',
              overflow: horizontalScroll ? 'auto' : 'visible !important'
            },
            '& .MuiDataGrid-footerContainer': {
              marginBottom: '1rem'
            },
            '& .MuiDataGrid-detailPanel': {
              border: '1px solid lightgray'
            },
            '& .MuiDataGrid-pinnedRowsRenderZone': {
              fontWeight: 'bolder'
            },
            '.grid-editable-cell': {
              backgroundColor: '#e0f7fa',
              color: '#004d40'
            },
            '.grid-editable-cell-disabled': {
              backgroundColor: '#f0f0f0',
              color: '#004d40'
            },
            px: 3,
            pr: 0,
            pl: 0
          }}
          slots={{
            detailPanelExpandIcon: ExpandMoreOutlined,
            detailPanelCollapseIcon: ExpandLessOutlined,
            noRowsOverlay: () => (
              <EmptyContent
                sx={{
                  py: 10
                }}
                title="No Data"
              />
            ),
            noResultsOverlay: () => (
              <EmptyContent
                sx={{
                  py: 10
                }}
                title="No results found"
              />
            )
          }}
        />
      </Box>
    );
  };

  const renderPaymentEntryFormContainer = () => {
    return (
      <FormProvider methods={paymentEntryMethods}>
        <Stack mt={2}>
          <Box px={3} mt={1}>
            <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
              <DropDownPanel
                title={props?.postingLevel || ''}
                collapse={serviceLineOpen.value}
                onCollapse={serviceLineOpen.onToggle}>
                {renderDataGridComponent('service_lines')}
              </DropDownPanel>
            </Card>
          </Box>
        </Stack>
      </FormProvider>
    );
  };

  const renderSelectInput = () => {
    return (
      <>
        {selectFields.map(
          ({name, placeholder, label, options, value, disable}) => (
            <FormControlDiv key={name}>
              <SelectInput
                name={name}
                disabled={disable}
                enableDefaultOption
                options={options || []}
                label={label}
                placeholder={placeholder}
                value={value || ''}
                fullWidth
                onChange={(e: any) =>
                  handleSelectFieldChange(name, e.target.value)
                }
                optionValueKey={'value'}
                optionLabelKey={'label'}
              />
            </FormControlDiv>
          )
        )}
      </>
    );
  };

  const renderPaymentType = () => {
    return (
      <FormControl>
        <FormLabel>Payment Source</FormLabel>
        <RadioGroup value={paymentType} onChange={handlePaymentTypeChange} row>
          <FormControlLabel
            disabled={
              pageDisabled || isEmptyServiceLines() || isSavedBatchDetail()
            }
            value="INSURANCE"
            control={<Radio />}
            label="Insurance"
          />
          <FormControlLabel
            disabled={
              pageDisabled || isEmptyServiceLines() || isSavedBatchDetail()
            }
            value="COLLECTION_AGENCY"
            control={<Radio />}
            label="Collection Agency"
          />
          <FormControlLabel
            disabled={
              pageDisabled || isEmptyServiceLines() || isSavedBatchDetail()
            }
            value="PATIENT"
            control={<Radio />}
            label="Patient"
          />
        </RadioGroup>
      </FormControl>
    );
  };

  return (
    <>
      <ListSubheaderComponent
        onClose={onClosePaymentEntry}
        label={props?.paymentPageLabel}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <Typography sx={{fontWeight: 'bold'}}>Payment Entry</Typography>
        {isSavedBatchDetail() ? (
          <Box px={3} sx={{display: 'flex', alignItems: 'center'}}>
            {isNewPayment || !pageDisabled ? (
              <Typography sx={{fontWeight: 'bold'}}>
                {isNewPayment ? 'New Entry' : 'Opened Entry(edit)'}
              </Typography>
            ) : (
              <>
                <IconButton
                  disabled={paymentPage <= 1 || isLoading}
                  onClick={handleLeft}
                  aria-label="Previous">
                  <KeyboardDoubleArrowLeft />
                </IconButton>
                <IconButton
                  disabled={paymentPage <= 1 || isLoading}
                  onClick={handlePrevious}
                  aria-label="Previous">
                  <ChevronLeft />
                </IconButton>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{fontWeight: 'bold', marginRight: 1}}>
                    Batch Entries
                  </Typography>

                  <TextField
                    disabled={isLoading}
                    type="number"
                    variant="outlined"
                    size="small"
                    value={inputPage}
                    onChange={e => {
                      setInputPage(e.target.value);
                    }}
                    onBlur={() => {
                      const newPage = parseInt(inputPage, 10);
                      if (
                        !isNaN(newPage) &&
                        newPage >= 1 &&
                        newPage <= totalPaymentPages
                      ) {
                        setPaymentPage(newPage);
                        getBatchDetails(newPage);
                      } else {
                        setInputPage(paymentPage);
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: totalPaymentPages,
                      style: {
                        textAlign: 'center',
                        width: '30px',
                        fontWeight: 'bold'
                      }
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{fontWeight: 'bold', marginLeft: 1}}>
                    {`of ${totalPaymentPages}`}
                  </Typography>
                </Box>
                <IconButton
                  disabled={paymentPage >= totalPaymentPages || isLoading}
                  onClick={handleNext}
                  aria-label="Next">
                  <ChevronRight />
                </IconButton>
                <IconButton
                  disabled={paymentPage >= totalPaymentPages || isLoading}
                  onClick={handleRight}
                  aria-label="Next">
                  <KeyboardDoubleArrowRight />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          <Typography sx={{fontWeight: 'bold'}}>
            {isNewPayment ? 'New Entry' : ''}
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <CircularProgressDiv>
          <LoadingScreen />
        </CircularProgressDiv>
      ) : (
        <>
          <Stack mt={2}>
            <Box px={3} mt={1}>
              <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
                <Box
                  mb={2}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                  <FlexDiv>
                    <div style={{marginBottom: 16}}>
                      <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <CardSpan style={{marginRight: 10, fontSize: 14}}>
                          Batch Name :
                        </CardSpan>
                        <Typography sx={{fontWeight: 'bold'}}>
                          {props?.batchInfo?.BatchName}
                        </Typography>
                      </Box>
                      <Box mt={1} sx={{display: 'flex', alignItems: 'center'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <CardSpan style={{marginRight: 10, fontSize: 14}}>
                            Batch Amount :
                          </CardSpan>
                          <Typography sx={{fontWeight: 'bold'}}>
                            {fCurrency(totalBatchAmount)}
                          </Typography>
                        </Box>
                        <Box
                          ml={2}
                          sx={{display: 'flex', alignItems: 'center'}}>
                          <CardSpan style={{marginRight: 10, fontSize: 14}}>
                            Posted Amount :
                          </CardSpan>
                          <Typography sx={{fontWeight: 'bold'}}>
                            {fCurrency(totalBatchPostedAmount)}
                          </Typography>
                        </Box>
                        <Box
                          ml={2}
                          sx={{display: 'flex', alignItems: 'center'}}>
                          <CardSpan style={{marginRight: 10, fontSize: 14}}>
                            Variance Amount :
                          </CardSpan>
                          <Typography sx={{fontWeight: 'bold'}}>
                            {fCurrency(totalBatchVarianceAmount)}
                          </Typography>
                        </Box>
                      </Box>
                    </div>
                  </FlexDiv>
                  {batchDetail.length && !isNewPayment ? (
                    <FlexDiv>
                      <div style={{marginBottom: 16}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <CardSpan style={{marginRight: 10}}>
                            Created By :
                          </CardSpan>
                          <Typography sx={{fontWeight: 'bold'}}>
                            {getCreatedByText()}
                          </Typography>
                        </Box>
                        <Box
                          mt={1}
                          sx={{display: 'flex', alignItems: 'center'}}>
                          <CardSpan style={{marginRight: 10}}>
                            Modified By :
                          </CardSpan>
                          <Typography sx={{fontWeight: 'bold'}}>
                            {getModifiedByText()}
                          </Typography>
                        </Box>
                      </div>
                    </FlexDiv>
                  ) : (
                    <div />
                  )}
                </Box>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(3, 1fr)'
                  }}>
                  <TextField
                    disabled={isSavedBatchDetail()}
                    value={accountNumber || ''}
                    label={'Account Number'}
                    fullWidth
                    placeholder={'Enter Account Number'}
                    onChange={(e: any) => {
                      setAccountNumber(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0000003b',
                        borderWidth: '1.8px'
                      }
                    }}
                  />
                  <TextField
                    disabled={isSavedBatchDetail()}
                    value={invoiceNumber || ''}
                    label={'Invoice Number'}
                    fullWidth
                    placeholder={'Enter Invoice Number'}
                    onChange={(e: any) => {
                      setInvoiceNumber(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0000003b',
                        borderWidth: '1.8px'
                      }
                    }}
                  />
                  <TextField
                    disabled={isSavedBatchDetail()}
                    value={MRN || ''}
                    label={'MRN'}
                    fullWidth
                    placeholder={'Enter MRN'}
                    onChange={(e: any) => {
                      setMRN(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0000003b',
                        borderWidth: '1.8px'
                      }
                    }}
                  />
                  <TextField
                    disabled
                    value={patientLastName || ''}
                    label={'Patient Last Name'}
                    fullWidth
                    placeholder={'Enter Last Name'}
                    onChange={(e: any) => {
                      setPatientLastName(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0000003b',
                        borderWidth: '1.8px'
                      }
                    }}
                  />
                  <TextField
                    disabled
                    value={patientFirstName || ''}
                    label={'Patient First Name'}
                    fullWidth
                    placeholder={'Enter First Name'}
                    onChange={(e: any) => {
                      setPatientFirstName(e.target.value);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0000003b',
                        borderWidth: '1.8px'
                      }
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}>
                  <LoadingButton
                    type="reset"
                    disabled={pageDisabled || isSavedBatchDetail()}
                    onClick={() => {
                      setPaymentPage(1);
                      setTotalPaymentPages(0);
                      resetState();
                    }}
                    variant="outlined"
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    Reset
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    disabled={pageDisabled || isSavedBatchDetail()}
                    loading={searchLoading}
                    onClick={() =>
                      getInvoices(
                        accountNumber,
                        invoiceNumber,
                        MRN,
                        batchDetail.length ? batchDetail[0] : {}
                      )
                    }
                    variant="contained"
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.dark
                    }}>
                    Search
                  </LoadingButton>
                </Box>
                {renderPaymentType()}
                <Box
                  mt={2}
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  p={1}
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: `repeat(${props?.gridItemsPerRow || 4}, 1fr)`
                  }}>
                  <>
                    {renderSelectInput()}
                    <TextField
                      disabled={pageDisabled || isEmptyServiceLines()}
                      value={trackingDescription || ''}
                      label={'Tracking Description'}
                      fullWidth
                      placeholder={'Enter Tracking Description'}
                      onChange={(e: any) => {
                        setTrackingDescription(e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0000003b',
                          borderWidth: '1.8px'
                        }
                      }}
                    />
                    <TextField
                      type="number"
                      disabled={
                        !(isCollectionAgencyPayment() || isPatientPayment()) ||
                        pageDisabled ||
                        isEmptyServiceLines()
                      }
                      value={getAmountValue(paymentAmount)}
                      label={'Payment Amount'}
                      fullWidth
                      placeholder={'Enter Payment Amount'}
                      onChange={(e: any) => {
                        setPaymentAmount(e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0000003b',
                          borderWidth: '1.8px'
                        }
                      }}
                    />
                    <TextField
                      type="number"
                      disabled={pageDisabled || isEmptyServiceLines()}
                      value={getAmountValue(adjustmentAmount)}
                      label={'Adjustment Amount'}
                      fullWidth
                      placeholder={'Enter Adjustment Amount'}
                      onChange={(e: any) => {
                        setAdjustmentAmount(e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0000003b',
                          borderWidth: '1.8px'
                        }
                      }}
                    />
                    <DatePicker
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0000003b',
                          borderWidth: '1.8px'
                        }
                      }}
                      label="Service Date"
                      value={serviceDate}
                      disabled={pageDisabled || isEmptyServiceLines()}
                      onChange={setServiceDate}
                    />
                  </>
                </Box>
                {isCollectionAgencyPayment() || isPatientPayment() ? (
                  <Box
                    sx={{
                      marginTop: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end'
                    }}>
                    <LoadingButton
                      type="submit"
                      loading={applyButtonLoading}
                      disabled={pageDisabled || (paymentAmount ? false : true)}
                      variant="contained"
                      onClick={onApplyButtonClick}
                      sx={{
                        marginLeft: '12px',
                        backgroundColor: theme.palette.primary.dark
                      }}>
                      {getApplyButtonText()}
                    </LoadingButton>
                  </Box>
                ) : null}
                <Box
                  sx={{
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}>
                  <LoadingButton
                    type="reset"
                    variant="outlined"
                    onClick={recalculatePaymentEntry}
                    loading={reCalculateButtonLoading}
                    disabled={pageDisabled || !serviceLinesRows.length}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    Recalculate
                  </LoadingButton>
                  <LoadingButton
                    onClick={onCobWindowClick}
                    type="reset"
                    variant="outlined"
                    disabled={pageDisabled || !serviceLinesRows.length}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    COB
                  </LoadingButton>
                  <LoadingButton
                    type="reset"
                    onClick={onNewPayment}
                    disabled={!pageDisabled}
                    variant="outlined"
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    New
                  </LoadingButton>
                  <LoadingButton
                    type="reset"
                    variant="outlined"
                    disabled={!pageDisabled}
                    onClick={() => setPageDisabled(false)}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    Open
                  </LoadingButton>
                  <LoadingButton
                    type="reset"
                    variant="outlined"
                    disabled={pageDisabled || isNewPayment}
                    onClick={onClearPaymentEntry}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.light
                    }}>
                    Clear
                  </LoadingButton>
                  <LoadingButton
                    type="reset"
                    variant="outlined"
                    onClick={() => setRemoveBatchDetailDialog(true)}
                    disabled={!isSavedBatchDetail() || !pageDisabled}
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: 'error.main'
                    }}>
                    Delete
                  </LoadingButton>
                  <LoadingButton
                    disabled={
                      getSaveDisabledStatus() || pageDisabled || !paymentType
                    }
                    type="submit"
                    loading={saveButtonLoading}
                    onClick={() => onSavePaymentEntry()}
                    variant="contained"
                    sx={{
                      marginLeft: '12px',
                      backgroundColor: theme.palette.primary.dark
                    }}>
                    Save
                  </LoadingButton>
                </Box>
              </Card>
            </Box>
          </Stack>
          {renderPaymentEntryFormContainer()}
          {removeBatchDetailDialog && (
            <ActionModalDialog
              open={removeBatchDetailDialog}
              onClose={onCloseRemoveBatchDetailDialog}
              title="Do you want to delete this Entry?"
              description="Deleting this entry will remove all the transactions which were posted."
              handleAgreeBtnClick={onRemoveBatchDetail}
              handleCloseBtnClick={onCloseRemoveBatchDetailDialog}
              loader={removeBatchDetailLoading}
              yesLabelText={'Yes'}
              noLabelText={'No'}
              yesButtonColor={APP.PALETTE.ROLES.RED}
              noButtonColor={APP.PALETTE.ROLES.GRAY}
            />
          )}
          {clearTransactionsDialog && (
            <ActionModalDialog
              open={clearTransactionsDialog}
              onClose={() => setClearTransactionsDialog(false)}
              title="Do you want to clear all transactions?"
              description="This action cannot be undone."
              handleAgreeBtnClick={clearPaymentEntryTransactions}
              handleCloseBtnClick={() => setClearTransactionsDialog(false)}
              loader={clearTransactionsLoading}
              yesLabelText={'Yes'}
              noLabelText={'No'}
              yesButtonColor={APP.PALETTE.ROLES.RED}
              noButtonColor={APP.PALETTE.ROLES.GRAY}
            />
          )}
          {displayCobWindow && (
            <Dialog
              fullWidth
              maxWidth="xl"
              open={displayCobWindow}
              onClose={onCloseCobWindow}
              transitionDuration={{
                enter: theme.transitions.duration.shortest,
                exit: 0
              }}
              PaperProps={{
                sx: {
                  overflow: 'unset'
                }
              }}
              sx={{
                [`& .${dialogClasses.container}`]: {
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                '& .MuiStack-root': {
                  borderRadius: '8px'
                }
              }}>
              <DialogTitle>Coordination of benefits (COB)</DialogTitle>
              <DialogContent>
                {renderDataGridComponent('cob_window')}
              </DialogContent>
              <DialogActions>
                <LoadingButton
                  type="reset"
                  variant="outlined"
                  onClick={onCloseCobWindow}
                  sx={{
                    marginLeft: '12px',
                    backgroundColor: theme.palette.primary.light
                  }}>
                  Cancel
                </LoadingButton>
                <LoadingButton
                  disabled={!cobPendingTransactions.length}
                  type="submit"
                  loading={saveButtonLoading}
                  onClick={() => saveCobPaymentEntryTransactions(batchDetail)}
                  variant="contained"
                  sx={{
                    marginLeft: '12px',
                    backgroundColor: theme.palette.primary.dark
                  }}>
                  Save
                </LoadingButton>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
    </>
  );
};

export default PaymentEntry;
